import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import { ApiResponse, PagedResponse, PaginationParams } from '../types/api';
import { LoginRequest, LoginResponse, RefreshTokenResponse } from '../types/auth';

/**
 * MindSpire Planner API Service
 * 
 * This service integrates with the unified API v2 structure that consolidates
 * endpoints while maintaining role-based access control.
 * 
 * Features:
 * - Unified profile management via /v2/profiles/*
 * - Unified schedule management via /v2/unified-schedule/*
 * - Unified exam management via /v2/exams/*
 * - Admin-only operations at /v1/admin/*
 * - Enhanced role-based error handling
 */
class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        // Enhance error with user-friendly message from backend
        this.enhanceError(error);
        
        const originalRequest = error.config;

        if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
          (originalRequest as any)._retry = true;

          try {
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            if (refreshToken) {
              const response = await this.client.post<ApiResponse<RefreshTokenResponse>>(
                '/v1/auth/refresh',
                { refreshToken }
              );
              
              const { accessToken } = response.data.data;
              localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
              
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.clearAuthData();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        // Handle role-based access control errors
        this.handleRoleBasedErrors(error);
        return Promise.reject(error);
      }
    );
  }

  // Public HTTP methods for direct API calls
  public get<T = any>(url: string, config?: any) {
    return this.client.get<T>(url, config);
  }

  public post<T = any>(url: string, data?: any, config?: any) {
    return this.client.post<T>(url, data, config);
  }

  public put<T = any>(url: string, data?: any, config?: any) {
    return this.client.put<T>(url, data, config);
  }

  public delete<T = any>(url: string, config?: any) {
    return this.client.delete<T>(url, config);
  }

  private handleRoleBasedErrors(error: AxiosError) {
    const { response } = error;
    
    switch (response?.status) {
      case 403:
        // Enhanced handling for role-based access denied
        const errorData = response.data as any;
        if (errorData?.message?.includes('Access denied')) {
          console.error('Role-based access denied:', errorData.message);
          // Could trigger a toast notification here
        } else {
          console.error('Access denied. Insufficient permissions.');
        }
        break;
      case 404:
        console.error('Requested resource not found.');
        break;
      case 500:
        console.error('Server error. Please try again later.');
        break;
      default:
        console.error('An unexpected error occurred:', error.message);
    }
  }

  private enhanceError(error: AxiosError) {
    if (error.response?.data) {
      const responseData = error.response.data as any;
      
      // Extract message from backend ApiResponse structure
      if (responseData.message) {
        (error as any).userMessage = responseData.message;
        (error as any).serverMessage = responseData.message;
      }
      
      // Extract validation errors if present
      if (responseData.errors && Array.isArray(responseData.errors)) {
        (error as any).validationErrors = responseData.errors;
        (error as any).userMessage = responseData.errors.join(', ');
      }
      
      // Log enhanced error for debugging
      console.error('API Error Enhanced:', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
        userMessage: (error as any).userMessage,
        serverMessage: (error as any).serverMessage,
        validationErrors: (error as any).validationErrors,
        originalData: responseData
      });
    }
  }

  private clearAuthData() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  // =====================================================
  // AUTHENTICATION (Unchanged)
  // =====================================================
  
  auth = {
    login: (data: LoginRequest) => 
      this.client.post<ApiResponse<LoginResponse>>('/v1/auth/login', data),
    
    refresh: () => 
      this.client.post<ApiResponse<RefreshTokenResponse>>('/v1/auth/refresh'),
    
    logout: () => 
      this.client.post<ApiResponse<void>>('/v1/auth/logout'),
    
    me: () => 
      this.client.get<ApiResponse<any>>('/v1/auth/me'),
  };

  // =====================================================
  // UNIFIED PROFILE MANAGEMENT (V2)
  // =====================================================
  
  profiles = {
    // Student profiles with role-based access
    getStudent: (id: number) => 
      this.client.get<ApiResponse<any>>(`/v2/profiles/students/${id}`),
    
    updateStudent: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v2/profiles/students/${id}`, data),
    
    getStudents: (params?: PaginationParams & { 
      search?: string, 
      gradeLevel?: number,
      status?: string,
      sort?: string, 
      direction?: string 
    }) => 
      this.client.get<ApiResponse<PagedResponse<any>>>('/v2/profiles/students', { params }),
    
    // Parent profiles with role-based access
    getParent: (id: number) => 
      this.client.get<ApiResponse<any>>(`/v2/profiles/parents/${id}`),
    
    updateParent: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v2/profiles/parents/${id}`, data),
    
    getParents: (params?: PaginationParams & { search?: string }) => 
      this.client.get<ApiResponse<PagedResponse<any>>>('/v2/profiles/parents', { params }),
  };

  // =====================================================
  // UNIFIED SCHEDULE MANAGEMENT (V2)
  // =====================================================
  
  schedule = {
    // Routines with role-based access
    createRoutine: (data: any) => 
      this.client.post<ApiResponse<any>>('/v2/routines', data),
    
    getStudentRoutines: (studentId: number, params?: { 
      includeInactive?: boolean, 
      type?: string, 
      detailLevel?: string 
    }) => 
      this.client.get<ApiResponse<any[]>>(`/v2/routines/student/${studentId}`, { params }),
    
    updateRoutine: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v2/routines/${id}`, data),
    
    reactivateRoutine: (id: number) => 
      this.client.post<ApiResponse<any>>(`/v2/routines/${id}/reactivate`),
    
    deleteRoutine: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/v2/routines/${id}`),
    
    // Phase 1: Exception management
    addRoutineException: (routineId: number, data: any) =>
      this.client.post<ApiResponse<any>>(`/v2/routines/${routineId}/exceptions`, data),
    
    removeRoutineException: (routineId: number, exceptionDate: string) =>
      this.client.delete<ApiResponse<any>>(`/v2/routines/${routineId}/exceptions/${exceptionDate}`),
    
    getRoutineExceptions: (routineId: number) =>
      this.client.get<ApiResponse<any[]>>(`/v2/routines/${routineId}/exceptions`),
    
    getRoutinesByDate: (studentId: number, date: string) =>
      this.client.get<ApiResponse<any[]>>(`/v2/routines/by-date`, { 
        params: { studentId, date } 
      }),
    
    // Study blocks with role-based access
    createStudyBlock: (data: any) => 
      this.client.post<ApiResponse<any>>('/v2/unified-schedule/study-blocks', data),
    
    getStudentStudyBlocks: (studentId: number, params?: {
      startDate?: string,
      endDate?: string,
      status?: string,
      studyBlockType?: string,
      page?: number,
      size?: number,
      sortBy?: string,
      sortDir?: string
    }) => 
      this.client.get<ApiResponse<PagedResponse<any>>>(`/v2/unified-schedule/study-blocks/student/${studentId}`, { params }),
    
    getStudyBlock: (id: number) => 
      this.client.get<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}`),
    
    updateStudyBlock: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}`, data),
    
    deleteStudyBlock: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/v2/unified-schedule/study-blocks/${id}`),
    
    startStudySession: (id: number) => 
      this.client.post<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}/start`),
    
    completeStudySession: (id: number) => 
      this.client.post<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}/complete`),
    
    getStudyBlocksForDate: (studentId: number, date: string) => 
      this.client.get<ApiResponse<any[]>>(`/v2/unified-schedule/study-blocks/student/${studentId}/date/${date}`),
    
    getStudyBlocksInDateRange: (studentId: number, startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any[]>>(`/v2/unified-schedule/study-blocks/student/${studentId}/range`, {
        params: { startDate, endDate }
      }),
    
    // Smart scheduling features (V2 - Enhanced AI-powered planning)
    // Note: Use smartPlanningApi for full smart planning functionality
    generateStudyPlan: (data: any) => 
      this.client.post<ApiResponse<any[]>>('/v2/planning/generate', data),
    
    getRecommendations: (studentId: number, type?: string) => 
      this.client.get<ApiResponse<string[]>>(`/v2/planning/recommendations/${studentId}${type ? `?type=${type}` : ''}`),
    
    detectConflicts: (studentId: number, startDate: string, endDate: string) => 
      this.client.post<ApiResponse<any>>('/v2/planning/conflicts', { studentId, startDate, endDate }),
    
    analyzeWorkload: (studentId: number, days: number = 7) => 
      this.client.get<ApiResponse<any>>(`/v2/planning/workload-analysis/${studentId}?days=${days}`),
    
    suggestTimeSlots: (studentId: number, subject: string, duration: number, preferredDate?: string) => {
      const params = new URLSearchParams({
        studentId: studentId.toString(),
        subject,
        duration: duration.toString()
      })
      if (preferredDate) params.append('preferredDate', preferredDate)
      return this.client.get<ApiResponse<any[]>>(`/v2/planning/time-suggestions?${params.toString()}`)
    },
  };

  // =====================================================
  // UNIFIED EXAM MANAGEMENT (V2)
  // =====================================================
  
  exams = {
    create: (data: any) => 
      this.client.post<ApiResponse<any>>('/api/v2/exams', data),
    
    get: (id: number) => 
      this.client.get<ApiResponse<any>>(`/api/v2/exams/${id}`),
    
    update: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/api/v2/exams/${id}`, data),
    
    delete: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/api/v2/exams/${id}`),
    
    getStudentExams: (studentId: number) => 
      this.client.get<ApiResponse<any[]>>(`/api/v2/exams/student/${studentId}`),
    
    getUpcoming: (studentId: number) => 
      this.client.get<ApiResponse<any[]>>(`/api/v2/exams/student/${studentId}/upcoming`),
    
    getByDateRange: (studentId: number, startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any[]>>(`/api/v2/exams/student/${studentId}/range`, {
        params: { startDate, endDate }
      }),
    
    getStatistics: (studentId: number) => 
      this.client.get<ApiResponse<any>>(`/api/v2/exams/student/${studentId}/statistics`),
    
    markCompleted: (id: number) => 
      this.client.post<ApiResponse<any>>(`/api/v2/exams/${id}/complete`),
    
    // Admin-only system-wide operations
    admin: {
      getAll: (params?: PaginationParams & { 
        search?: string, 
        status?: string, 
        examType?: string, 
        startDate?: string, 
        endDate?: string 
      }) => 
        this.client.get<ApiResponse<PagedResponse<any>>>('/api/v2/exams', { params }),
      
      getSystemStatistics: () => 
        this.client.get<ApiResponse<any>>('/api/v2/exams/statistics'),
      
      bulkUpdateStatus: (data: any) => 
        this.client.post<ApiResponse<any>>('/api/v2/exams/bulk-update-status', data),
      
      exportExams: (params?: any) => 
        this.client.get('/api/v2/exams/export', { 
          params, 
          responseType: 'blob',
          headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        }),
    },
  };

  // =====================================================
  // ADMIN-ONLY OPERATIONS (V1 - Unchanged)
  // =====================================================
  
  admin = {
    // Account management (create/delete only)
    createStudentAccount: (data: any) => 
      this.client.post<ApiResponse<any>>('/v1/admin/accounts/students', data),
    
    deleteStudentAccount: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/v1/admin/accounts/students/${id}`),
    
    hardDeleteStudentAccount: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/v1/admin/accounts/students/${id}/hard?confirm=CONFIRM_PERMANENT_DELETE`),
    
    reactivateStudentAccount: (id: number) => 
      this.client.post<ApiResponse<void>>(`/v1/admin/accounts/students/${id}/reactivate`),
    
    createParentAccount: (data: any) => 
      this.client.post<ApiResponse<any>>('/v1/admin/accounts/parents', data),
    
    deleteParentAccount: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/v1/admin/accounts/parents/${id}`),
    
    // Enhanced parent deletion operations
    deactivateParent: (id: number) =>
      this.client.put<ApiResponse<void>>(`/v1/admin/accounts/parents/${id}/deactivate`),
    
    softDeleteParent: (id: number) =>
      this.client.delete<ApiResponse<void>>(`/v1/admin/accounts/parents/${id}/soft`),
    
    permanentDeleteParent: (id: number) =>
      this.client.delete<ApiResponse<void>>(`/v1/admin/accounts/parents/${id}/permanent`),
    
    restoreParent: (id: number) =>
      this.client.put<ApiResponse<void>>(`/v1/admin/accounts/parents/${id}/restore`),
    
    // System management
    getDashboardOverview: () => 
      this.client.get<ApiResponse<any>>('/v1/admin/system/dashboard/overview'),
    
    getSystemHealth: () => 
      this.client.get<ApiResponse<any>>('/v1/admin/system/health'),
    
    getPerformanceMetrics: () => 
      this.client.get<ApiResponse<any>>('/v1/admin/system/performance-metrics'),

    // Analytics methods (using AdminAnalyticsService)
    getSystemOverview: () => 
      this.client.get<ApiResponse<any>>('/v1/admin/analytics/overview'),
    
    getUserEngagementMetrics: (startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any>>(`/v1/admin/analytics/engagement?startDate=${startDate}&endDate=${endDate}`),
    
    getAnalyticsPerformanceMetrics: (startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any>>(`/v1/admin/analytics/performance?startDate=${startDate}&endDate=${endDate}`),
    
    getSystemUsageStats: (startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any>>(`/v1/admin/analytics/usage?startDate=${startDate}&endDate=${endDate}`),
    
    generateComprehensiveReport: (startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any>>(`/v1/admin/analytics/report?startDate=${startDate}&endDate=${endDate}`),
    
    // Family management
    familyManagement: {
      codes: {
        list: (params?: PaginationParams) => 
          this.client.get<PagedResponse<any>>('/v1/admin/family/codes', { params }),
        
        getByStudent: (studentId: number, params?: PaginationParams) => 
          this.client.get<PagedResponse<any>>(`/v1/admin/family/codes/student/${studentId}`, { params }),
        
        getActive: () => 
          this.client.get<ApiResponse<any[]>>('/v1/admin/family/codes/active'),
        
        generate: (data: { studentId: number, maxUses?: number, expiresInDays?: number }) => 
          this.client.post<ApiResponse<any>>('/v1/admin/family/codes', data),
        
        deactivate: (id: number) => 
          this.client.patch<ApiResponse<void>>(`/v1/admin/family/codes/${id}/deactivate`),
        
        delete: (id: number) => 
          this.client.delete<ApiResponse<void>>(`/v1/admin/family/codes/${id}`),
      },
      
      links: {
        getByStudent: (studentId: number) => 
          this.client.get<ApiResponse<any[]>>(`/v1/admin/family/links/student/${studentId}`),
        
        getByParent: (parentId: number) => 
          this.client.get<ApiResponse<any[]>>(`/v1/admin/family/links/parent/${parentId}`),
        
        remove: (linkId: number) => 
          this.client.delete<ApiResponse<void>>(`/v1/admin/family/links/${linkId}`),
      },
      
      statistics: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/family/statistics'),
      
      // Parent family operations
      parent: {
        getLinkedStudents: () => 
          this.client.get<ApiResponse<any[]>>('/parent/family/linked-students'),
        
        getStudentLink: (studentId: number) => 
          this.client.get<ApiResponse<any>>(`/parent/family/student/${studentId}`),
        
        linkStudent: (data: { code: string, parentId: number, relationship?: string }) => 
          this.client.post<ApiResponse<any>>('/v1/family/link', data),
      },
    },
    
    // Analytics (v2 endpoints)
    analytics: {
      // Dashboard overview - combines multiple analytics for dashboard
      overview: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/summary'),
      
      // Usage analytics
      usage: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/usage'),
      
      // Performance analytics
      performance: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/performance'),
      
      // Error analytics
      errors: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/errors'),
      
      // Smart planning analytics
      smartPlanning: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/smart-planning'),
      
      // Progressive complexity analytics
      progressiveComplexity: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/progressive-complexity'),
      
      // System health status
      health: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/health'),
      
      // Real-time metrics
      realtime: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/realtime'),
      
      // Export all analytics data
      export: () => 
        this.client.get<ApiResponse<any>>('/v2/analytics/export'),
    },
    
    // Curriculum Template Management (V1 - Admin CRUD operations)
    // Note: Use adminCurriculumApi for full admin functionality
    getCurriculumTemplates: (params?: PaginationParams & { subject?: string, activeOnly?: boolean }) => 
      this.client.get<PagedResponse<any>>('/admin/curriculum-templates', { params }),
    
    curriculumTemplates: {
      create: (data: any) => 
        this.client.post<ApiResponse<any>>('/admin/curriculum-templates', data),
      
      update: (id: number, data: any) => 
        this.client.put<ApiResponse<any>>(`/admin/curriculum-templates/${id}`, data),
      
      get: (id: number) => 
        this.client.get<ApiResponse<any>>(`/admin/curriculum-templates/${id}`),
      
      getAll: (params?: PaginationParams & { activeOnly?: boolean }) => 
        this.client.get<PagedResponse<any>>('/admin/curriculum-templates', { params }),
      
      delete: (id: number) => 
        this.client.delete<ApiResponse<void>>(`/admin/curriculum-templates/${id}`),
    },

    // Template Units Management
    templateUnits: {
      getByTemplate: (templateId: number) => 
        this.client.get<ApiResponse<any[]>>(`/v1/admin/template-units/template/${templateId}`),
      
      get: (unitId: number) => 
        this.client.get<ApiResponse<any>>(`/v1/admin/template-units/${unitId}`),
      
      create: (templateId: number, data: any) => 
        this.client.post<ApiResponse<any>>(`/v1/admin/template-units/template/${templateId}`, data),
      
      update: (unitId: number, data: any) => 
        this.client.put<ApiResponse<any>>(`/v1/admin/template-units/${unitId}`, data),
      
      delete: (unitId: number) => 
        this.client.delete<ApiResponse<void>>(`/v1/admin/template-units/${unitId}`),
      
      reorder: (templateId: number, unitIds: number[]) => 
        this.client.put<ApiResponse<void>>(`/v1/admin/template-units/template/${templateId}/reorder`, { unitIds }),
    },

    // Template Topics Management
    templateTopics: {
      getByUnit: (unitId: number) => 
        this.client.get<ApiResponse<any[]>>(`/v1/admin/template-topics/unit/${unitId}`),
      
      getByTemplate: (templateId: number) => 
        this.client.get<ApiResponse<any[]>>(`/v1/admin/template-topics/template/${templateId}`),
      
      get: (topicId: number) => 
        this.client.get<ApiResponse<any>>(`/v1/admin/template-topics/${topicId}`),
      
      create: (unitId: number, data: any) => 
        this.client.post<ApiResponse<any>>(`/v1/admin/template-topics/unit/${unitId}`, data),
      
      update: (topicId: number, data: any) => 
        this.client.put<ApiResponse<any>>(`/v1/admin/template-topics/${topicId}`, data),
      
      delete: (topicId: number) => 
        this.client.delete<ApiResponse<void>>(`/v1/admin/template-topics/${topicId}`),
      
      reorder: (unitId: number, topicIds: number[]) => 
        this.client.put<ApiResponse<void>>(`/v1/admin/template-topics/unit/${unitId}/reorder`, { topicIds }),
    },
    
    // Admin Dashboard (v1 endpoints - working)
    dashboard: {
      // Main dashboard data
      overview: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard'),
      
      // System analytics
      analytics: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/analytics'),
      
      // Performance metrics
      performance: (startDate: string, endDate: string) => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/performance', { 
          params: { startDate, endDate } 
        }),
      
      // System health
      health: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/health'),
      
      // Quick stats for dashboard widgets
      stats: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/stats'),
      
      // Enrollment trends
      enrollmentTrends: (months?: number) => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/trends/enrollments', { 
          params: months ? { months } : undefined 
        }),
      
      activity: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/activity'),
      
      content: () => 
        this.client.get<ApiResponse<any>>('/v1/admin/dashboard/content'),
    },

    // Parent Management (merged from second admin object) - REMOVED DUPLICATES
    // Note: createParent -> use createParentAccount
    // Note: updateParent -> use profiles.updateParent  
    // Note: deleteParent -> use deleteParentAccount
    
    // Enhanced parent management
    getParentsByStatus: (params: { status?: string, search?: string, page?: number, size?: number, sortBy?: string, sortDir?: string }) =>
      this.client.get<ApiResponse<PagedResponse<any>>>('/v1/admin/parents/enhanced', { params }),
    
    getParentStatistics: () =>
      this.client.get<ApiResponse<any>>('/v1/admin/parents/enhanced/statistics'),
    
    toggleParentStatus: (id: number) =>
      this.client.put<ApiResponse<any>>(`/v1/admin/parents/enhanced/${id}/toggle-status`),
    
    generateParentToken: (parentId: number) => 
      this.client.post<ApiResponse<any>>(`/v1/admin/parents/${parentId}/generate-token`),
  };

  // =====================================================
  // PARENT-SPECIFIC OPERATIONS
  // =====================================================
  
  parent = {
    // Get linked students for the authenticated parent
    getLinkedStudents: () => 
      this.client.get<ApiResponse<any[]>>('/parent/family/linked-students'),
    
    // Get specific student link details
    getStudentLink: (studentId: number) => 
      this.client.get<ApiResponse<any>>(`/parent/family/student/${studentId}`),
    
    // Dashboard and notifications
    dashboard: (id: number) => 
      this.client.get<ApiResponse<any>>(`/v1/parents/${id}/dashboard`),
    
    notifications: (id: number, params?: PaginationParams) => 
      this.client.get<PagedResponse<any>>(`/v1/parents/${id}/notifications`, { params }),
    
    markNotificationsRead: (id: number, notificationIds: number[]) => 
      this.client.post<ApiResponse<string>>(`/v1/parents/${id}/notifications/mark-read`, notificationIds),
    
    // Today's activities and weekly plans
    todayBlocks: (id: number) => 
      this.client.get<ApiResponse<any>>(`/v1/parents/${id}/study-blocks/today`),
    
    weeklyPlan: (id: number, weekStart?: string) => 
      this.client.get<ApiResponse<any>>(`/v1/parents/${id}/study-blocks/weekly`, {
        params: weekStart ? { weekStart } : undefined
      }),
  };

  // =====================================================
  // STUDENT INTERFACE
  // =====================================================
  
  student = {
    // Get student's own profile and progress
    getProfile: (studentId: number) => 
      this.client.get<ApiResponse<any>>(`/v2/profiles/students/${studentId}`),
    
    // Get student's schedule and activities
    getTodayActivities: (studentId: number, date: string) => 
      this.client.get<ApiResponse<any[]>>(`/v2/unified-schedule/study-blocks/student/${studentId}/date/${date}`),
    
    getWeeklySchedule: (studentId: number, startDate: string, endDate: string) => 
      this.client.get<ApiResponse<any[]>>(`/v2/unified-schedule/study-blocks/student/${studentId}/range`, {
        params: { startDate, endDate }
      }),
    
    // Get student's routines
    getRoutines: (studentId: number, params?: { activeOnly?: boolean }) => 
      this.client.get<ApiResponse<any[]>>(`/v2/routines/student/${studentId}`, { 
        params: params?.activeOnly ? { includeInactive: false } : undefined 
      }),
    
    // Get student's study blocks
    getStudyBlocks: (studentId: number, params?: { startDate?: string, endDate?: string }) => 
      this.client.get<ApiResponse<PagedResponse<any>>>(`/v2/unified-schedule/study-blocks/student/${studentId}`, { params }),
    
    // Create study block
    createStudyBlock: (data: any) => 
      this.client.post<ApiResponse<any>>('/v2/unified-schedule/study-blocks', data),
    
    // Update study block
    updateStudyBlock: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}`, data),
    
    // Start/Complete study block
    startStudyBlock: (id: number) => 
      this.client.post<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}/start`),
    
    completeStudyBlock: (id: number) => 
      this.client.post<ApiResponse<any>>(`/v2/unified-schedule/study-blocks/${id}/complete`),
    
    // Get student's exams
    getExams: (studentId: number, params?: { upcoming?: boolean, limit?: number, past?: boolean }) => {
      if (params?.upcoming) {
        return this.client.get<ApiResponse<any[]>>(`/api/v2/exams/student/${studentId}/upcoming`)
      }
      if (params?.past) {
        return this.client.get<ApiResponse<any[]>>(`/api/v2/exams/student/${studentId}`, { params: { past: true } })
      }
      return this.client.get<ApiResponse<any[]>>(`/api/v2/exams/student/${studentId}`)
    },
    
    // Get achievements and milestones
    getAchievements: (studentId: number) => 
      this.client.get<ApiResponse<any[]>>(`/v2/profiles/students/${studentId}/achievements`),
    
    // Course enrollment management
    getCourseEnrollments: (studentId: number) => 
      this.client.get<ApiResponse<any[]>>(`/v2/profiles/students/${studentId}/enrollments`),
    
    enrollInCourse: (studentId: number, curriculumTemplateId: number) => 
      this.client.post<ApiResponse<any>>(`/v2/profiles/students/${studentId}/enrollments`, { curriculumTemplateId }),
    
    unenrollFromCourse: (studentId: number, enrollmentId: number) => 
      this.client.delete<ApiResponse<void>>(`/v2/profiles/students/${studentId}/enrollments/${enrollmentId}`),
    
    // Student curriculum browsing (V2 - Read-only access for students/parents)
    // Note: Use curriculumBrowsingApi for full browsing functionality
    getAvailableCurriculumTemplates: (params?: any) => 
      this.client.get<ApiResponse<PagedResponse<any>>>(`/v2/curriculum-templates`, { params }),
    
    getCurriculumTemplateDetails: (templateId: number) => 
      this.client.get<ApiResponse<any>>(`/v2/curriculum-templates/${templateId}`),
    
    getDetailedCurriculumTemplate: (templateId: number) => 
      this.client.get<ApiResponse<any>>(`/v2/curriculum-templates/${templateId}/detailed`),
    
    getAvailableSubjects: () => 
      this.client.get<ApiResponse<string[]>>(`/v2/curriculum-templates/subjects`),
    
    getAvailableGradeLevels: () => 
      this.client.get<ApiResponse<number[]>>(`/v2/curriculum-templates/grade-levels`),
    
    getCurriculumStats: () => 
      this.client.get<ApiResponse<any>>(`/v2/curriculum-templates/stats`),
    
    // Student progress tracking
    getProgress: (studentId: number, params?: { timeFilter?: string }) => 
      this.client.get<ApiResponse<any>>(`/v2/profiles/students/${studentId}/progress`, { params }),
    
    // Student exam management
    createExam: (data: any) => 
      this.client.post<ApiResponse<any>>('/api/v2/exams', data),
    
    updateExam: (id: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/api/v2/exams/${id}`, data),
    
    deleteExam: (id: number) => 
      this.client.delete<ApiResponse<void>>(`/api/v2/exams/${id}`),
  };

  // =====================================================
  // NOTIFICATIONS
  // =====================================================
  
  notifications = {
    send: (data: any) => 
      this.client.post<ApiResponse<any>>('/v1/notifications/send', data),
    
    getUserNotifications: (userId: number, params?: PaginationParams) => 
      this.client.get<PagedResponse<any>>(`/v1/notifications/user/${userId}`, { params }),
    
    markAsRead: (id: number) => 
      this.client.put<ApiResponse<void>>(`/v1/notifications/${id}/read`),
    
    getPreferences: (userId: number) => 
      this.client.get<ApiResponse<any>>(`/v1/notifications/preferences/${userId}`),
    
    updatePreferences: (userId: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v1/notifications/preferences/${userId}`, data),
  };

  // =====================================================
  // DEVICE REGISTRATION
  // =====================================================
  
  devices = {
    register: (data: any) => 
      this.client.post<ApiResponse<any>>('/v1/devices/register', data),
    
    update: (deviceId: string, data: any) => 
      this.client.put<ApiResponse<any>>(`/v1/devices/${deviceId}`, data),
    
    unregister: (deviceId: string) => 
      this.client.delete<ApiResponse<void>>(`/v1/devices/${deviceId}`),
    
    getUserDevices: (userId: number) => 
      this.client.get<ApiResponse<any[]>>(`/v1/devices/user/${userId}`),
  };

  // =====================================================
  // PARENT ALERTS
  // =====================================================
  
  parentAlerts = {
    create: (data: any) => 
      this.client.post<ApiResponse<any>>('/v1/parent/alerts', data),
    
    update: (alertSettingId: number, data: any) => 
      this.client.put<ApiResponse<any>>(`/v1/parent/alerts/${alertSettingId}`, data),
    
    getAll: () => 
      this.client.get<ApiResponse<any[]>>('/v1/parent/alerts'),
    
    delete: (alertSettingId: number) => 
      this.client.delete<ApiResponse<void>>(`/v1/parent/alerts/${alertSettingId}`),
    
    enable: (alertSettingId: number) => 
      this.client.put<ApiResponse<any>>(`/v1/parent/alerts/${alertSettingId}/enable`),
    
    disable: (alertSettingId: number) => 
      this.client.put<ApiResponse<any>>(`/v1/parent/alerts/${alertSettingId}/disable`),
  };

  // =====================================================
  // AUTO PLANNING
  // =====================================================
  
  autoPlan = {
    generateExamPreparation: (data: any) => 
      this.client.post<ApiResponse<any[]>>('/api/auto-plan/exam-preparation', data),
    
    generateCatchUp: (data: any) => 
      this.client.post<ApiResponse<any[]>>('/api/auto-plan/catch-up', data),
    
    getSmartSuggestions: (studentId: number) => 
      this.client.get<ApiResponse<string[]>>('/api/auto-plan/suggestions', { params: { studentId } }),
    
    generateWeeklySchedule: (data: any) => 
      this.client.post<ApiResponse<any[]>>('/api/auto-plan/weekly-schedule', data),
  };

  // =====================================================
  // SMART PLANNING (V2)
  // =====================================================
  
  smartPlanning = {
    generateStudyPlan: (data: any) => 
      this.client.post<ApiResponse<any[]>>('/v2/smart-planning/generate', data),
    
    detectConflicts: (data: any) => 
      this.client.post<ApiResponse<any>>('/v2/smart-planning/conflicts', data),
    
    getRecommendations: (studentId: number) => 
      this.client.get<ApiResponse<any[]>>(`/v2/smart-planning/recommendations/${studentId}`),
    
    // Legacy methods for backward compatibility
    generateWeeklyPlan: (studentId: number, weekStart?: string) => 
      this.client.post<ApiResponse<any[]>>(`/v2/smart-planning/weekly-plan/${studentId}`, 
        weekStart ? { weekStart } : {}),
    
    optimizeSchedule: (data: any) => 
      this.client.post<ApiResponse<any>>('/v2/smart-planning/optimize-schedule', data),
  };

  // =====================================================
  // PERFORMANCE MONITORING (V2)
  // =====================================================
  
  performance = {
    getMetrics: () => 
      this.client.get<ApiResponse<any>>('/v2/performance/metrics'),
    
    getOptimizationSuggestions: () => 
      this.client.get<ApiResponse<any>>('/v2/performance/optimization'),
    
    runBenchmark: (data: any) => 
      this.client.post<ApiResponse<any>>('/v2/performance/benchmark', data),
  };

  // =====================================================
  // DOCUMENTATION (V2)
  // =====================================================
  
  documentation = {
    getOpenApiSpec: () => 
      this.client.get<ApiResponse<any>>('/v2/docs/openapi'),
    
    getTroubleshootingGuide: () => 
      this.client.get<ApiResponse<any>>('/v2/docs/troubleshooting'),
  };

  // =====================================================
  // HEALTH AND SYSTEM STATUS
  // =====================================================
  
  health = {
    check: () => 
      this.client.get<ApiResponse<any>>('/v1/health'),
    
    ping: () => 
      this.client.get<ApiResponse<string>>('/v1/health/ping'),
    
    version: () => 
      this.client.get<ApiResponse<any>>('/v1/health/version'),
  };




}

export const apiService = new ApiService();
export default apiService;